/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import Icon from '@material-ui/core/Icon';
import {
  ClearAll as ClearAllIcon,
  DateRange as DateRangeIcon,
  GetApp as GetAppIcon,
  Search as SearchIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { toastr } from 'react-redux-toastr';
import * as XLSX from 'xlsx';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingProgress from 'common/components/progress/loading';
import {
  ATALHOS_DE_PERIODO,
  LIMITE_DE_EXPORTACAO,
  RECURRENCE_LABELS,
  SOURCE_CONFIG,
  buscarVendas,
  datasDoPeriodo,
  formatCurrency,
  formatDate,
  mapPaymentMethod,
  paramsDosFiltros,
  recurrenceLabel,
  statusLabel,
  statusStyle,
  textoDoPeriodo,
} from './salesReportShared';

const useStyles = makeStyles((theme) => ({
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 0,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    padding: theme.spacing(2, 2.25),
    minWidth: 0,
  },
  statIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 46,
    height: 46,
    borderRadius: 0,
    backgroundColor: '#eef2ff',
    color: '#4338ca',
    flexShrink: 0,
    '& .material-icons': { fontSize: 24 },
  },
  statBody: { minWidth: 0 },
  statLabel: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#64748b',
    lineHeight: 1.3,
    marginBottom: 2,
  },
  statValue: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#0f172a',
    lineHeight: 1.15,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  panel: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 0,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2.5),
  },
  panelLabel: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#94a3b8',
    marginBottom: theme.spacing(2),
  },
  clearButton: { textTransform: 'none', fontWeight: 600, color: '#64748b' },
  actionButton: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: theme.spacing(0.5),
    boxShadow: 'none',
  },
  presetChip: { borderRadius: 0, fontWeight: 600, fontSize: '0.78rem' },
  periodInfo: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    fontSize: '0.85rem',
    color: '#475569',
  },
  periodStrong: { fontWeight: 700, color: '#0f172a' },
  tableContainer: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 0,
    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)',
    overflowX: 'auto',
    position: 'relative',
    '& .p-datatable': { fontSize: '13px', border: 'none' },
    '& .p-datatable-thead > tr > th': {
      backgroundColor: '#f8fafc',
      color: '#475569',
      fontWeight: 600,
      fontSize: '0.72rem',
      letterSpacing: 0.3,
      textTransform: 'uppercase',
      padding: theme.spacing(1.5, 1.25),
      borderColor: '#e2e8f0',
      whiteSpace: 'nowrap',
    },
    '& .p-datatable-tbody > tr > td': {
      padding: theme.spacing(1.25, 1.25),
      borderTop: '1px solid #f1f5f9',
      color: '#1e293b',
      fontSize: '0.85rem',
      whiteSpace: 'nowrap',
    },
    '& .p-datatable-tbody > tr:nth-of-type(even)': { backgroundColor: '#fbfcfe' },
    '& .p-datatable-tbody > tr:hover': { backgroundColor: '#eef2ff !important' },
    '& .p-datatable-tbody > tr.linha-clicavel': { cursor: 'pointer' },
    '& .p-paginator': {
      borderTop: '1px solid #e2e8f0',
      padding: theme.spacing(0.5, 1.5),
      backgroundColor: '#ffffff',
    },
    '& .p-paginator .p-dropdown': {
      borderRadius: 0,
      border: '1px solid #cbd5f5',
      backgroundColor: '#f8fafc',
    },
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
    background: 'rgba(255,255,255,0.6)',
  },
  money: { fontVariantNumeric: 'tabular-nums', fontWeight: 600 },
  moneyMuted: { fontVariantNumeric: 'tabular-nums', color: '#94a3b8' },
  primaryText: { color: '#1e293b', lineHeight: 1.3 },
  secondaryText: { color: '#94a3b8', fontSize: '0.75rem', lineHeight: 1.3 },
  chip: { fontWeight: 600, fontSize: '0.72rem', height: 22, borderRadius: 0 },
  emptyState: { padding: theme.spacing(8, 3), textAlign: 'center', color: '#64748b' },
  emptyIcon: { fontSize: 48, color: '#cbd5e1', marginBottom: theme.spacing(1) },
}));

const FILTROS_INICIAIS = {
  startDate: '',
  endDate: '',
  allHistory: false,
  source: 'all',
  paidOnly: false,
};

/**
 * Lista de vendas da estação — links de pagamento, pedidos de produto e
 * assinaturas — com busca e filtros resolvidos no servidor.
 *
 * Sem datas, mostra o mês corrente; com texto na busca, o que faltar de data
 * vale para o histórico inteiro (quem procura um comprador não sabe o mês da
 * compra). Os filtros ficam sempre na tela, inclusive com o resultado vazio.
 */
export default function SalesReportView({
  stationId,
  showKpis,
  onRowClick,
  isRowClickable,
  onResult,
  refreshKey,
  renderExtraActions,
}) {
  const classes = useStyles();

  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [data, setData] = useState({ list: [], metrics: null, total: 0, period: null });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const requestSeq = useRef(0);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(
    () => paramsDosFiltros({ ...filtros, search: debouncedSearch, page, pageSize }),
    [filtros, debouncedSearch, page, pageSize]
  );

  const fetchSales = useCallback(async () => {
    if (!stationId) return;
    const seq = (requestSeq.current += 1);
    setLoading(true);
    try {
      const resposta = await buscarVendas(stationId, params);
      if (seq !== requestSeq.current) return;
      const novo = {
        list: resposta.list || [],
        metrics: resposta.metrics || null,
        total: resposta.total || 0,
        period: resposta.period || null,
      };
      setData(novo);
      if (onResultRef.current) onResultRef.current({ ...novo, params });
    } catch (error) {
      if (seq === requestSeq.current) {
        toastr.error(
          'Erro',
          error?.response?.data?.message || 'Não foi possível carregar as vendas.'
        );
      }
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [stationId, params]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales, refreshKey]);

  const mudarFiltros = (parcial) => {
    setFiltros((atual) => ({ ...atual, ...parcial }));
    setPage(0);
  };

  const mudarBusca = (valor) => {
    setSearch(valor);
    setPage(0);
  };

  const aplicarAtalho = (atalho) => {
    mudarFiltros({ ...atalho.range(), allHistory: atalho.key === 'all' });
  };

  const atalhoAtivo = useMemo(() => {
    if (filtros.allHistory && !filtros.startDate && !filtros.endDate) return 'all';
    const igual = ATALHOS_DE_PERIODO.find((a) => {
      if (a.key === 'all') return false;
      const r = a.range();
      return r.startDate === filtros.startDate && r.endDate === filtros.endDate;
    });
    return igual ? igual.key : null;
  }, [filtros]);

  const temDatas = Boolean(filtros.startDate || filtros.endDate);
  const activeFilterCount =
    (filtros.source !== 'all' ? 1 : 0) +
    (filtros.paidOnly ? 1 : 0) +
    (debouncedSearch ? 1 : 0) +
    (temDatas || filtros.allHistory ? 1 : 0);

  const limparFiltros = () => {
    setFiltros(FILTROS_INICIAIS);
    setSearch('');
    setDebouncedSearch('');
    setPage(0);
  };

  const buscarEmTodoHistorico = () =>
    mudarFiltros({ startDate: '', endDate: '', allHistory: true });

  const { metrics } = data;
  const kpiCards = [
    { icon: 'shopping_cart', label: 'Vendas no período', value: metrics ? metrics.totalCount : 0 },
    { icon: 'check_circle', label: 'Vendas pagas', value: metrics ? metrics.paidCount : 0 },
    { icon: 'payments', label: 'Bruto faturado', value: formatCurrency(metrics?.grossPaid) },
    {
      icon: 'account_balance_wallet',
      label: 'Líquido faturado',
      value: formatCurrency(metrics?.netPaid),
    },
  ];

  const tableRows = useMemo(
    () =>
      data.list.map((row, idx) => ({
        ...row,
        rowId: `${row.source}:${row.order_id}:${row.cycle_number || ''}:${idx}`,
      })),
    [data.list]
  );

  const bySourceSummary = useMemo(() => {
    if (!metrics?.bySource) return [];
    return Object.entries(metrics.bySource).map(([key, v]) => ({
      key,
      label: SOURCE_CONFIG[key]?.label || key,
      count: v.count,
      paidCount: v.paidCount,
    }));
  }, [metrics]);

  const sourceBody = (row) => {
    const cfg = SOURCE_CONFIG[row.source] || { label: row.source, bg: '#eef2ff', fg: '#4338ca' };
    return (
      <Chip
        label={cfg.label}
        size="small"
        className={classes.chip}
        style={{ backgroundColor: cfg.bg, color: cfg.fg }}
      />
    );
  };
  const customerBody = (row) => (
    <Box>
      <div className={classes.primaryText}>{row.customer_name || '—'}</div>
      {row.cpf ? <div className={classes.secondaryText}>{row.cpf}</div> : null}
    </Box>
  );
  const refBody = (row) => (
    <Box>
      <div className={classes.primaryText}>{row.sale_ref || '—'}</div>
      {row.gateway_ref ? (
        <div className={classes.secondaryText} title="ID na Pagar.me">
          {row.gateway_ref}
        </div>
      ) : null}
    </Box>
  );
  const grossBody = (row) => <span className={classes.money}>{formatCurrency(row.gross)}</span>;
  const netBody = (row) =>
    row.net === null || row.net === undefined ? (
      <span className={classes.moneyMuted}>—</span>
    ) : (
      <span className={classes.money}>{formatCurrency(row.net)}</span>
    );
  const statusBody = (row) => (
    <Chip label={statusLabel(row)} size="small" className={classes.chip} style={statusStyle(row)} />
  );
  const recurrenceBody = (row) => {
    const label = recurrenceLabel(row);
    if (!label) return <span style={{ color: '#94a3b8' }}>—</span>;
    return (
      <span style={{ color: '#4338ca', fontWeight: 600 }}>
        {label}
        {row.cycle_number ? ` · #${row.cycle_number}` : ''}
      </span>
    );
  };

  const podeClicar = (row) => Boolean(onRowClick) && (!isRowClickable || isRowClickable(row));

  const emptyBody = () => {
    let dica = 'Escolha outro período para encontrar vendas anteriores.';
    let acao = null;
    if (debouncedSearch && temDatas) {
      dica = 'A busca está limitada ao período escolhido.';
      acao = 'Buscar em todo o histórico';
    } else if (activeFilterCount) {
      dica = 'Ajuste ou limpe os filtros para ver mais resultados.';
    } else {
      acao = 'Ver todo o histórico';
    }
    return (
      <div className={classes.emptyState}>
        <Icon className={classes.emptyIcon}>
          {activeFilterCount ? 'filter_alt_off' : 'receipt_long'}
        </Icon>
        <Typography style={{ fontWeight: 600, color: '#475569' }}>
          {activeFilterCount ? 'Nenhuma venda com os filtros atuais' : 'Nenhuma venda neste mês'}
        </Typography>
        <Typography variant="body2" style={{ marginTop: 4 }}>
          {dica}
        </Typography>
        {acao && (
          <Button
            size="small"
            color="primary"
            variant="outlined"
            style={{ marginTop: 12, textTransform: 'none' }}
            onClick={buscarEmTodoHistorico}>
            {acao}
          </Button>
        )}
      </div>
    );
  };

  const handleExport = async () => {
    if (!stationId || !data.total) return;
    setExporting(true);
    try {
      const resposta = await buscarVendas(stationId, {
        ...params,
        page: 0,
        pageSize: LIMITE_DE_EXPORTACAO,
      });
      const all = resposta.list || [];
      const rows = all.map((row) => ({
        Fonte: SOURCE_CONFIG[row.source]?.label || row.source,
        Referência: row.sale_ref,
        'ID Pagar.me': row.gateway_ref || '',
        Data: formatDate(row.sale_date),
        Cliente: row.customer_name || '',
        CPF: row.cpf || '',
        'E-mail': row.email || '',
        Telefone: row.phone || '',
        Descrição: row.description || '',
        'Valor bruto': Number(row.gross || 0),
        'Valor líquido': row.net === null || row.net === undefined ? '' : Number(row.net),
        'Forma de pagamento': mapPaymentMethod(row.payment_method),
        Status: statusLabel(row),
        Pago: Number(row.is_paid) ? 'Sim' : 'Não',
        Recorrente: Number(row.is_recurring) ? 'Sim' : 'Não',
        Recorrência: Number(row.is_recurring)
          ? RECURRENCE_LABELS[row.recurrence] || row.recurrence || ''
          : '',
        Ciclo: row.cycle_number || '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Vendas');
      const datas = datasDoPeriodo(resposta.period);
      const sufixo =
        resposta.period?.allHistory || !datas ? 'historico' : `${datas.start}_a_${datas.end}`;
      XLSX.writeFile(wb, `Relatorio_Vendas_${sufixo}.xlsx`);
      if (all.length >= LIMITE_DE_EXPORTACAO) {
        toastr.warning(
          'Exportação parcial',
          'Há mais de 10.000 vendas com esses filtros; refine o período para exportar tudo.'
        );
      } else {
        toastr.success('Exportado', `${all.length} venda(s) exportada(s).`);
      }
    } catch (error) {
      toastr.error('Erro', 'Falha ao exportar as vendas.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      {showKpis && (
        <div className={classes.statGrid}>
          {kpiCards.map((kpi) => (
            <div className={classes.statCard} key={kpi.label}>
              <div className={classes.statIcon}>
                <Icon>{kpi.icon}</Icon>
              </div>
              <div className={classes.statBody}>
                <div className={classes.statLabel}>{kpi.label}</div>
                <div className={classes.statValue} title={String(kpi.value)}>
                  {kpi.value}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Box className={classes.panel}>
        <div className={classes.panelLabel}>
          <DateRangeIcon style={{ fontSize: 16 }} />
          Filtros
          {activeFilterCount > 0 && (
            <Chip
              label={`${activeFilterCount} ativo${activeFilterCount > 1 ? 's' : ''}`}
              size="small"
              style={{
                height: 20,
                borderRadius: 0,
                backgroundColor: '#eef2ff',
                color: '#4338ca',
                fontWeight: 700,
              }}
            />
          )}
          <Box flexGrow={1} />
          {activeFilterCount > 0 && (
            <Button
              size="small"
              className={classes.clearButton}
              startIcon={<ClearAllIcon />}
              onClick={limparFiltros}>
              Limpar
            </Button>
          )}
          {renderExtraActions ? renderExtraActions({ params, period: data.period }) : null}
          <Button
            size="small"
            variant="contained"
            color="primary"
            className={classes.actionButton}
            startIcon={<GetAppIcon />}
            onClick={handleExport}
            disabled={exporting || !data.total}>
            {exporting ? 'Exportando…' : 'Exportar Excel'}
          </Button>
        </div>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Buscar por nome, CPF, e-mail, atividade, ID do pedido ou ID Pagar.me"
              value={search}
              onChange={(e) => mudarBusca(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#94a3b8', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              label="Fonte"
              SelectProps={{ native: true }}
              value={filtros.source}
              onChange={(e) => mudarFiltros({ source: e.target.value })}>
              <option value="all">Todas as fontes</option>
              <option value="link">Links de pagamento</option>
              <option value="produto">Pedidos de produto</option>
              <option value="assinatura">Assinaturas</option>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              variant="outlined"
              label="Data início"
              InputLabelProps={{ shrink: true }}
              value={filtros.startDate}
              onChange={(e) => mudarFiltros({ startDate: e.target.value, allHistory: false })}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              variant="outlined"
              label="Data fim"
              InputLabelProps={{ shrink: true }}
              value={filtros.endDate}
              onChange={(e) => mudarFiltros({ endDate: e.target.value, allHistory: false })}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={filtros.paidOnly}
                  onChange={(e) => mudarFiltros({ paidOnly: e.target.checked })}
                />
              }
              label={<span style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Só pagas</span>}
            />
          </Grid>
        </Grid>

        <Box mt={2} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
          {ATALHOS_DE_PERIODO.map((atalho) => (
            <Chip
              key={atalho.key}
              label={atalho.label}
              size="small"
              clickable
              className={classes.presetChip}
              color={atalhoAtivo === atalho.key ? 'primary' : 'default'}
              variant={atalhoAtivo === atalho.key ? 'default' : 'outlined'}
              onClick={() => aplicarAtalho(atalho)}
            />
          ))}
        </Box>

        <div className={classes.periodInfo}>
          <span>
            Período:{' '}
            <span className={classes.periodStrong}>{textoDoPeriodo(data.period) || '—'}</span>
          </span>
          {!temDatas && !filtros.allHistory && (
            <span style={{ color: '#94a3b8' }}>
              {debouncedSearch
                ? '· sem datas, a busca procura em todo o histórico'
                : '· sem datas, a lista mostra o mês atual'}
            </span>
          )}
          {bySourceSummary.map((s) => (
            <Chip
              key={s.key}
              size="small"
              className={classes.chip}
              style={{
                backgroundColor: SOURCE_CONFIG[s.key]?.bg,
                color: SOURCE_CONFIG[s.key]?.fg,
              }}
              label={`${s.label}: ${s.count} (${s.paidCount} pagas)`}
            />
          ))}
        </div>
      </Box>

      <Box className={classes.tableContainer}>
        {loading && (
          <Box className={classes.loadingOverlay}>
            <LoadingProgress />
          </Box>
        )}
        <DataTable
          value={tableRows}
          dataKey="rowId"
          lazy
          paginator
          first={page * pageSize}
          rows={pageSize}
          totalRecords={data.total}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPage={(e) => {
            setPage(e.page);
            setPageSize(e.rows);
          }}
          onRowClick={(e) => {
            if (podeClicar(e.data)) onRowClick(e.data);
          }}
          rowClassName={(row) => (podeClicar(row) ? 'linha-clicavel' : '')}
          emptyMessage={emptyBody()}
          currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} vendas"
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown">
          <Column header="Fonte" body={sourceBody} style={{ minWidth: 110 }} />
          <Column
            header="Data"
            body={(row) => formatDate(row.sale_date)}
            style={{ minWidth: 100 }}
          />
          <Column header="Cliente" body={customerBody} style={{ minWidth: 200 }} />
          <Column field="description" header="Descrição" style={{ minWidth: 220 }} />
          <Column header="ID" body={refBody} style={{ minWidth: 170 }} />
          <Column
            header="Valor"
            body={grossBody}
            style={{ minWidth: 110 }}
            bodyStyle={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            header="Líquido"
            body={netBody}
            style={{ minWidth: 110 }}
            bodyStyle={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            header="Pagamento"
            body={(row) => mapPaymentMethod(row.payment_method)}
            style={{ minWidth: 140 }}
          />
          <Column header="Status" body={statusBody} style={{ minWidth: 120 }} />
          <Column header="Recorrência" body={recurrenceBody} style={{ minWidth: 150 }} />
        </DataTable>
      </Box>
    </>
  );
}

SalesReportView.propTypes = {
  isRowClickable: PropTypes.func,
  onResult: PropTypes.func,
  onRowClick: PropTypes.func,
  refreshKey: PropTypes.number,
  renderExtraActions: PropTypes.func,
  showKpis: PropTypes.bool,
  stationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

SalesReportView.defaultProps = {
  isRowClickable: undefined,
  onResult: undefined,
  onRowClick: undefined,
  refreshKey: 0,
  renderExtraActions: undefined,
  showKpis: true,
  stationId: null,
};
